import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, User, LogOut, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/flights", label: "Flights" },
  { to: "/hotels", label: "Hotels" },
  { to: "/packages", label: "Packages" },
  { to: "/deals", label: "Deals" },
  { to: "/destinations", label: "Destinations" },
  { to: "/visa-guides", label: "Visa" },
  { to: "/blog", label: "Blog" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all ${scrolled ? "glass border-b border-slate-200 shadow-sm" : "bg-white"}`}
    >
      <div className="container-page flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-brand text-white font-montserrat font-extrabold text-lg shadow-md">M</span>
          <div className="leading-tight">
            <div className="font-montserrat font-extrabold text-slate-900 text-lg">MyTripPoint</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 hidden sm:block">Your Journey Starts Here</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "text-brand bg-brand-50" : "text-slate-700 hover:text-brand hover:bg-slate-50"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user && typeof user === "object" ? (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
                <User size={16} className="text-brand" />
                <span className="text-sm font-medium text-slate-700">{user.name || user.email}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => { await logout(); navigate("/"); }}
                data-testid="logout-btn"
              >
                <LogOut size={16} /> Logout
              </Button>
            </div>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link">
                <Button variant="ghost" size="sm">Login</Button>
              </Link>
              <Link to="/register" data-testid="header-register-link">
                <Button size="sm" className="btn-brand text-white">Sign Up</Button>
              </Link>
            </>
          )}
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-slate-200"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <nav className="container-page py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-lg text-sm font-semibold ${
                    isActive ? "text-brand bg-brand-50" : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="border-t border-slate-200 pt-3 mt-2">
              {user && typeof user === "object" ? (
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={async () => { await logout(); setOpen(false); navigate("/"); }}
                >
                  <LogOut size={16} /> Logout {user.name || user.email}
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Link to="/login" onClick={() => setOpen(false)} className="flex-1">
                    <Button variant="outline" className="w-full">Login</Button>
                  </Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="flex-1">
                    <Button className="w-full btn-brand text-white">Sign Up</Button>
                  </Link>
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
